import { memo, useCallback, useState } from 'react'
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material'
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Work as WorkIcon,
  Create as CreateIcon,
} from '@mui/icons-material'
import { useAppDispatch } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import ConfirmAction from 'src/v3/components/confirm-action'
import { AuditLogIcon } from 'src/v3/core/icons'
import AuditLogModal from '../../components/audit-log-modal'
import { fetchSelectedUser } from '../../manage.slice'
import { SelectedUser } from '../types'

interface RoleManagementCardProps {
  user: SelectedUser
  sessionUserId: string | undefined
  linkedServiceID: string
  onSuccess: (message: string) => void
  onError: (message: string) => void
  onRemoveFromRoster: () => void
}

export let RoleManagementCard = memo(
  ({
    user,
    sessionUserId,
    linkedServiceID,
    onSuccess,
    onError,
    onRemoveFromRoster,
  }: RoleManagementCardProps) => {
    let dispatch = useAppDispatch()
    let [confirmDialogOpen, setConfirmDialogOpen] = useState<string>('')
    let [auditLogOpen, setAuditLogOpen] = useState<boolean>(false)

    let isManager = user.roles?.includes('manager') || false
    let isCreator = user.roles?.includes('creator') || false

    let toggleRole = (
      targetUser: SelectedUser,
      role: 'manager' | 'creator',
    ) => {
      let newRoles = targetUser.roles?.includes(role)
        ? targetUser.roles?.filter((name) => name !== role)
        : [...(targetUser.roles || []), role]

      return axios.put('/api/v2/manage/user/roles', {
        userID: targetUser.id,
        roles: newRoles,
      })
    }

    let removeUserFromRoster = (userID: string, serviceID: string) =>
      axios.delete(
        `/api/v2/manage/roster/user?userID=${userID}&linkedServiceID=${serviceID}`,
      )

    let handleRoleToggle = useCallback(
      async (role: 'manager' | 'creator') => {
        if (!user) return

        try {
          await toggleRole(user, role)
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(fetchSelectedUser(user.id))
          onSuccess(
            `${role} role ${user.roles?.includes(role) ? 'removed from' : 'added to'} ${user.firstName}`,
          )
        } catch (error: any) {
          // eslint-disable-next-line no-console
          console.error('Role toggle error:', error)
          onError(
            error?.response?.data || error?.message || 'Error updating role',
          )
        }
      },
      [user, dispatch, onSuccess, onError],
    )

    let handleRemoveFromRoster = useCallback(async () => {
      if (!user) return

      try {
        await removeUserFromRoster(user.id, linkedServiceID)
        onSuccess(`${user.firstName} ${user.lastName} removed from roster`)
        onRemoveFromRoster()
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('Remove from roster error:', error)
        onError(
          error?.response?.data ||
            error?.message ||
            'Error removing user from roster',
        )
      }
    }, [user, linkedServiceID, onSuccess, onError, onRemoveFromRoster])

    return (
      <>
        <Box className="lg:col-span-2">
          <Card className="w-full flex flex-col h-full">
            <CardContent className="p-8">
              <Typography
                variant="h6"
                className="mb-6 font-semibold"
                id="role-management-heading"
              >
                Role Management
              </Typography>

              <Box
                className="mb-8"
                role="region"
                aria-labelledby="role-management-heading"
              >
                <Typography
                  variant="subtitle2"
                  className="mb-4"
                  id="current-roles-label"
                >
                  Current Roles:
                </Typography>
                <Box
                  className="grid max-w-sm grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 sm:gap-3"
                  role="group"
                  aria-labelledby="current-roles-label"
                >
                  {isManager && (
                    <Chip
                      icon={<WorkIcon aria-hidden="true" />}
                      label="Manager"
                      color="primary"
                      variant="filled"
                      aria-label="User has Manager role"
                    />
                  )}
                  {isCreator && (
                    <Chip
                      icon={<CreateIcon aria-hidden="true" />}
                      label="Creator"
                      color="secondary"
                      variant="filled"
                      aria-label="User has Creator role"
                    />
                  )}
                  <Chip
                    icon={<PersonAddIcon aria-hidden="true" />}
                    label="Student"
                    color="default"
                    variant="filled"
                    aria-label="User has Student role (default)"
                  />
                </Box>
              </Box>

              <Typography
                variant="subtitle2"
                className="mb-4"
                id="role-actions-label"
              >
                Actions:
              </Typography>
              <Box
                className="grid max-w-2xl grid-cols-1 gap-3 md:grid-cols-2"
                role="group"
                aria-labelledby="role-actions-label"
              >
                <Button
                  variant="outlined"
                  disabled={sessionUserId === user?.id}
                  startIcon={
                    isManager ? (
                      <PersonRemoveIcon aria-hidden="true" />
                    ) : (
                      <PersonAddIcon aria-hidden="true" />
                    )
                  }
                  onClick={() => handleRoleToggle('manager')}
                  aria-label={`${isManager ? 'Remove' : 'Add'} Manager role for ${user?.firstName} ${user?.lastName}`}
                  aria-describedby={
                    sessionUserId === user?.id
                      ? 'cannot-modify-own-roles'
                      : undefined
                  }
                >
                  {isManager ? 'Remove' : 'Add'} Manager Role
                </Button>
                <Button
                  variant="outlined"
                  disabled={sessionUserId === user?.id}
                  startIcon={
                    isCreator ? (
                      <PersonRemoveIcon aria-hidden="true" />
                    ) : (
                      <PersonAddIcon aria-hidden="true" />
                    )
                  }
                  onClick={() => handleRoleToggle('creator')}
                  aria-label={`${isCreator ? 'Remove' : 'Add'} Creator role for ${user?.firstName} ${user?.lastName}`}
                  aria-describedby={
                    sessionUserId === user?.id
                      ? 'cannot-modify-own-roles'
                      : undefined
                  }
                >
                  {isCreator ? 'Remove' : 'Add'} Creator Role
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={sessionUserId === user?.id}
                  startIcon={<PersonRemoveIcon aria-hidden="true" />}
                  onClick={() => setConfirmDialogOpen(`remove-${user?.id}`)}
                  aria-label={`Remove ${user?.firstName} ${user?.lastName} from roster`}
                  aria-describedby={
                    sessionUserId === user?.id
                      ? 'cannot-remove-self'
                      : undefined
                  }
                >
                  Remove from Roster
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AuditLogIcon aria-hidden="true" />}
                  onClick={() => setAuditLogOpen(true)}
                  aria-label={`View audit logs for ${user?.firstName} ${user?.lastName}`}
                >
                  View Audit Logs
                </Button>
                {sessionUserId === user?.id && (
                  <>
                    <span id="cannot-modify-own-roles" className="sr-only">
                      You cannot modify your own roles
                    </span>
                    <span id="cannot-remove-self" className="sr-only">
                      You cannot remove yourself from the roster
                    </span>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
        <AuditLogModal
          open={auditLogOpen}
          setOpen={setAuditLogOpen}
          userID={user?.id}
          title={`Assignment Credit Audit Logs - ${user?.firstName} ${user?.lastName}`}
        />
        <ConfirmAction
          executeAction={handleRemoveFromRoster}
          open={confirmDialogOpen}
          setOpen={setConfirmDialogOpen}
          openText={`remove-${user?.id}`}
          title="Confirm Roster Removal"
          text={`Are you sure you want to remove ${user?.firstName} ${user?.lastName} from this linked service's roster?`}
          onFailure={(err) =>
            onError(err?.toString() || 'Error removing user from roster')
          }
        />
      </>
    )
  },
)

export default RoleManagementCard
